import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";
import moment from "moment";
import { createClient } from "graphql-ws";
import { NativeAudio } from "@capacitor-community/native-audio";
import * as Sentry from '@sentry/vue';
import { name, version } from '../../package.json';

Vue.use(Vuex);
let timeOutId = null
// Routine lifecycle activity (connecting/connected/closed) is log
// information and stays breadcrumb-only; actual errors (data fetch
// failures, subscription errors, and client-level websocket errors) are
// always reported to Sentry. Sentry's dedupeIntegration groups identical
// errors server-side, and getInitialData/getProcessDisplayData are only
// polled hourly (see App.vue), so there's no need to dedupe those captures
// client-side.
//
// The one exception is the graphql-ws client-level `on.error` handler
// below: it fires on every retry attempt with a fixed 10s `retryWait`, so
// an unguarded capture would flood Sentry with an event roughly every 10
// seconds during a sustained outage. That one flag stays.
let websocketErrorReported = false

// Service-agnostic client-identification headers so backends (e.g.
// sc-graph-api) can attribute traffic to this frontend.
axios.defaults.headers.common['x-sc-client-name'] = name;
axios.defaults.headers.common['x-sc-client-version'] = version;

// A string hashing function
const cyrb53 = (str, seed = 0) => {
  let h1 = 0xdeadbeef ^ seed, h2 = 0x41c6ce57 ^ seed;
  for(let i = 0, ch; i < str.length; i++) {
      ch = str.charCodeAt(i);
      h1 = Math.imul(h1 ^ ch, 2654435761);
      h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1  = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2  = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
};

const getWsClientOptions = (state, commit, dispatch) => {
  return {
    url: state.socketURL,
    retryAttempts: 9000,
    retryWait: async function randomisedExponentialBackoff(
    ) {
      let retryDelay = 10000; // start with 1s delay
      await new Promise((resolve) =>
        setTimeout(
          resolve,
          retryDelay
        )
      );
    },
    generateID: (payload) => {
      let subscriptionId = state.uuid

      try {
        const payloadAsString = JSON.stringify(payload);
        const payloadAsDeterministicHash = cyrb53(payloadAsString);
        subscriptionId = state.uuid + ':' + payloadAsDeterministicHash
      }
      catch(e) {
        // Do nothing
      }

      return subscriptionId
    },
    shouldRetry: () => {
      return true;
    },
    keepAlive: 15000,
    on: {
      connecting: () => {
        commit("updatedSocketClientState", "connecting");

        Sentry.addBreadcrumb({
          category: 'websocket',
          message: 'WebSocket is connecting',
          level: 'info',
          data: {
            processDisplaySystem: state.processDisplaySystem?.uuid,
          },
        });
      },
      connected: (socket) => {
        commit("updatedSocketClientState", "connected");
        commit("updateActiveSocket", socket)
        websocketErrorReported = false;
        dispatch("getOrderData");

        if(state.socketURL.includes('localhost') && state.simulateBadWebSocketConnection === true) {
          setTimeout(() => {
            socket.close(4205, 'Client Restart. Simulating bad client connection.');
          }, 3000)
        }

        Sentry.addBreadcrumb({
          category: 'websocket',
          message: 'WebSocket is connected',
          level: 'info',
          data: {
            processDisplaySystem: state.processDisplaySystem?.uuid,
          },
        });
      },
      closed: (event) => {
        commit("updatedSocketClientState", "closed");
        commit("updateActiveSocket", null)
        Sentry.addBreadcrumb({
          category: 'websocket',
          message: 'WebSocket is closed',
          level: 'info',
          data: {
            processDisplaySystem: state.processDisplaySystem?.uuid,
            code: event?.code,
            reason: event?.reason,
          },
        });
      },
      error: (error) => {
        commit("updatedSocketClientState", "error");
        // This fires on every retry attempt (fixed 10s retryWait,
        // retryAttempts: 9000), so this flag guards against flooding Sentry
        // with an event roughly every 10 seconds during a sustained outage.
        Sentry.addBreadcrumb({
          category: 'websocket',
          message: 'WebSocket has error',
          level: 'info',
          data: {
            processDisplaySystem: state.processDisplaySystem?.uuid,
            error: error instanceof Error ? error.message : error,
          },
        });

        if (websocketErrorReported === false) {
          websocketErrorReported = true;
          Sentry.captureException(error);
        }
      },
      ping: (received) => {
          if (!received) { // sent
            if (timeOutId) clearTimeout(timeOutId); // Clear any existing timeout
            timeOutId = setTimeout(() => {
              if (state.activeSocket !== null && state.activeSocket.readyState === WebSocket.OPEN) {
                Sentry.addBreadcrumb({
                  category: 'websocket',
                  message: 'Closing socket due to pong timeout',
                  level: 'info',
                  data: {
                    processDisplaySystemUUID: state.uuid,
                  },
                });
                state.activeSocket.close(4408, 'Request Timeout due to no ping within timeframe.')
              }
            }, 60000)
        }
      },
      pong: (received) => {
        if (received) {
          clearTimeout(timeOutId); // pong is received, clear connection close timeout
        }
      },
    },
    connectionParams: () => {
      return {
        headers: {
          Authorization:
            "ReadAccessToken " +
            state.processDisplaySystem
              .readAccessToken.token,
          "SalesCloud-Organization":
            state.processDisplaySystem.organization
              .uuid,
          "x-sc-client-name": name,
          "x-sc-client-version": version,
        },
      };
    },
  }
}

export default new Vuex.Store({
  state: {
    graphURL: process.env.VUE_APP_GRAPHQL_URL,
    socketURL: process.env.VUE_APP_GRAPHQL_WS_URL,
    appLoading: false,
    processDisplaySystem: null,
    simulateBadWebSocketConnection: false,
    uuid: "",
    audio: new Audio(process.env.BASE_URL + "messenger-sound.mp3"),
    organizationUUID: "",
    organization: null,
    locationOptions: null,
    channelOptions: null,
    categoryOptions: null,
    itemMap: null,
    processing: false,
    socketClient: null,
    socketClientState: null,
    subscribedToPDS: false,
    activeSocket: null,
    orders: [],
    posOrders: [],
    selectedOrders: [],
    date: new Date(),
    orderStates: [
      {
        key: "new",
        label: "New",
        color: "yellow darken-2",
        nextStep: "processing",
        previousStep: null,
      },
      {
        key: "pending",
        label: "Pending",
        color: "blue",
        nextStep: "processing",
        previousStep: "new",
      },
      {
        key: "processing",
        label: "Processing",
        color: "brown",
        nextStep: "completed",
        previousStep: "new",
      },
      {
        key: "completed",
        label: "Completed",
        color: "green",
        nextStep: "delivered",
        previousStep: "processing",
      },
      {
        key: "delivered",
        label: "Delivered",
        color: "grey",
        nextStep: null,
        previousStep: "completed",
      },
    ],
  },
  mutations: {
    updateActiveSocket(state, socket) {
      state.activeSocket = socket
    },
    updateSubscribedToPDS(state, value) {
      state.subscribedToPDS = value;
    },
    updatedSocketClientState(state, clientState) {
      state.socketClientState = clientState;
    },
    setProcessDisplaySystem(state, processDisplaySystem) {
      if (
        typeof processDisplaySystem !== "undefined" &&
        processDisplaySystem !== null
      ) {
        try {
          localStorage.setItem(
            "salescloud:kds:object",
            JSON.stringify(processDisplaySystem)
          );
        } catch {
          // Do nothing
        }
        state.processDisplaySystem = processDisplaySystem;
        window.processDisplaySystem = processDisplaySystem;
      }
    },
    setProcessing(state, processing) {
      state.processing = processing;
    },
    updateSocketClient(state, socketClient) {
      state.socketClient = socketClient;
    },
    updateAppLoading(state, appLoading) {
      state.appLoading = appLoading;
    },
    updatePDSUUID(state, uuid) {
      if (uuid === null || uuid === undefined) {
        return;
      }

      state.uuid = uuid;
    },
    updateOrganization(state, organization) {
      if (organization === null || organization === undefined) {
        return;
      }

      state.organization = organization;
      state.organizationUUID = organization.uuid;
    },
    updateOrganizationUUID(state, uuid) {
      if (uuid === null || uuid === undefined) {
        return;
      }

      state.organizationUUID = uuid;
    },
    updateLocationOptions(state, locationOptions) {
      if (locationOptions === null || locationOptions === undefined) {
        return;
      }

      state.locationOptions = locationOptions;
    },
    updateChannelOptions(state, channelOptions) {
      if (channelOptions === null || channelOptions === undefined) {
        return;
      }

      state.channelOptions = channelOptions;
    },
    updateCategoryOptions(state, categoryOptions) {
      if (categoryOptions === null || categoryOptions === undefined) {
        return;
      }

      state.categoryOptions = categoryOptions;
    },
    updateItemMap(state, itemArray) {
      const itemMap = {};

      for (let i = 0; i < itemArray.length; i++) {
        const currentItem = itemArray[i];
        itemMap[currentItem.uuid] = currentItem;
      }

      state.itemMap = itemMap;
    },
    updateOrderChanged(state, order) {
      if(typeof order === "undefined" || order === null) {
        return;
      }

      const orderIndex = state.orders.findIndex(existingOrder => existingOrder !== undefined && existingOrder !== null && existingOrder.uuid === order.uuid)
      console.log('updateOrderChanged', orderIndex);

      if(orderIndex > -1) {
        state.orders.splice(orderIndex, 1, order);
      }
      else {
        state.orders.push(order)
        try {
          NativeAudio.play({
            assetId: "ding2",
          }).catch(() => {
            // Do nothing
          });
        } catch {
          // Do nothing
        }
      }

    },
    updatePosOrderChanged(state, order) {

      if(typeof order === "undefined" || order === null) {
        return;
      }

      const orderIndex = state.posOrders.findIndex(existingOrder => existingOrder !== undefined && existingOrder !== null && existingOrder.uuid === order.uuid);

      if(orderIndex > -1) {
        state.posOrders.splice(orderIndex, 1, order);
      }
      else {
        state.posOrders.push(order)
        try {
          NativeAudio.play({
            assetId: "ding2",
          }).catch(() => {
            // Do nothing
          });
        } catch {
          // Do nothing
        }
      }

    },
    updateOrders(state, orders) {
      if (typeof orders === "undefined" || orders === null) {
        return;
      }
      //This should be turned into a function BUT NOT TODAY :D
      orders = orders.filter(
        (order) =>(
          Array.isArray(order.lineItems) &&
          order.lineItems.length > 0  && 
          typeof order.state !== "undefined" &&
          order.state !== null &&
          order.state !== "cart" && 
          (
            (
              Array.isArray(order.payments) &&
              order.payments.length > 0 &&
              order.payments.some(
              (payment) =>
                payment !== null &&
                payment !== undefined &&
                payment.status === "success"
              )
            ) || 
            order.lineItems.some((lineItem) => lineItem.printed) || 
            order.origin === 'deliverect' || 
            (
              typeof order.balance !== "undefined" &&
              order.balance !== null &&
              typeof order.balance.amount !== "undefined" &&
              order.balance !== null &&
              order.balance.amount === 0 
            )
          )
        )
      )

      if (state.orders.length < orders.length) {
        try {
          NativeAudio.play({
            assetId: "ding2",
          }).catch(() => {
            // Do nothing
          });
        } catch {
          // Do nothing
        }
      }

      state.orders = orders;
    },
    // if balance == 0, lineitems > 0 || printed lineitems
    updatePosOrders(state, orders) {
      if (typeof orders === "undefined" || orders === null) {
        return;
      }

      orders = orders.filter(
        (order) =>
          ((typeof order.balance !== "undefined" &&
            order.balance !== null &&
            order.balance.amount == 0 &&
            order.lineItems.length > 0) ||
            (Array.isArray(order.lineItems) &&
              order.lineItems.length > 0 &&
              order.lineItems.some((lineItem) => lineItem.printed))) &&
              typeof order.state !== "undefined" &&
              order.state !== null &&
              order.state !== "cart"
      );

      if (state.posOrders.length < orders.length) {
        try {
          NativeAudio.play({
            assetId: "ding2",
          }).catch(() => {
            // Do nothing
          });
        } catch {
          // Do nothing
        }
      }
      state.posOrders = orders;
    },
    setOrderState(state, payload) {
      for (let i in state.orders) {
        if (state.orders[i].uuid === payload.uuid) {
          let orderState = payload.state;

          if (orderState === "pending") {
            orderState = "new";
          }

          state.orders[i].state = orderState;
        }
      }
    },
    setPosOrderState(state, payload) {
      for (let i in state.posOrders) {
        if (state.posOrders[i].uuid === payload.uuid) {
          let orderState = payload.state;

          if (orderState === "pending") {
            orderState = "new";
          }

          state.posOrders[i].state = orderState;
        }
      }
    },
    selectOrder(state, order) {
      const index = state.selectedOrders.findIndex(
        (o) => o.uuid === order.uuid
      );
      if (index === -1) {
        state.selectedOrders.push(order);
      } else {
        state.selectedOrders.splice(index, 1);
      }
    },
    deselectAllOrders(state) {
      state.selectedOrders = [];
    },
  },
  getters: {
    startTimeInMillis: (state) => {
      return moment(state.date).startOf("day").valueOf() - 3600000 * 4; //Buffer four hours in millis
    },
    endTimeInMillis: (state) => {
      return state.date.getTime();
    },
    selectedStates: (state) => {
      if (
        state.processDisplaySystem === null ||
        typeof state.processDisplaySystem === "undefined" ||
        !Array.isArray(state.processDisplaySystem.states) ||
        state.processDisplaySystem.states.length < 1
      ) {
        return [];
      }
      return state.processDisplaySystem.states;
    },
  },
  actions: {
    subscribeToProcessDisplaySystem: (
      { state, commit, dispatch },
      processDisplaySystem
    ) => {
      commit("updateSubscribedToPDS", true);
      const body = {
        query:
          "subscription pds($uuid: String) {\n" +
          "  processDisplaySystem(uuid: $uuid){\n" +
          "    uuid\n" +
          "    title\n" +
          "    debug\n" +
          "    organization {\n" +
          "      uuid\n" +
          "      label\n" +
          "      primary_color\n" +
          "      accent_color\n" +
          "    }\n" +
          "    channels {\n" +
          "      uuid\n" +
          "    }\n" +
          "    posChannels {\n" +
          "      uuid\n" +
          "    }\n" +
          "    location {\n" +
          "      label\n" +
          "      uuid\n" +
          "      ept\n" +
          "      timezone\n" +
          "    }\n" +
          "    categories {\n" +
          "      uuid\n" +
          "    }\n" +
          "    states\n" +
          "    stateColors {\n" +
          "      new\n" +
          "      pending\n" +
          "      processing\n" +
          "      completed\n" +
          "      delivered\n" +
          "    }\n" +
          "    readAccessToken {\n" +
          "      token\n" +
          "    }\n" +
          "    timeOfRed\n" +
          "    timeOfYellow\n" +
          "    clickableLineItems\n" +
          "    colorWholeCard\n" +
          "    showLocationVariationOnOrderCard\n" +
          "    darkMode\n" +
          "    printers {\n" +
          "       model\n" +
          "       ipAddress\n" +
          "    }\n" +
          "  }\n" +
          "}",
        variables: {
          uuid: processDisplaySystem.uuid,
        },
      };

      state.socketClient.subscribe(body, {
        next: (data) => {
          commit("setProcessDisplaySystem", data.data.processDisplaySystem);
          dispatch("getOrderData");
        },
        error: (error) => {
          Sentry.addBreadcrumb({
            category: 'subscription',
            message: 'processDisplaySystem subscription error',
            level: 'info',
            data: {
              processDisplaySystem: state.processDisplaySystem?.uuid,
              error: error instanceof Error ? error.message : error,
            },
          });

          Sentry.captureException(error);
        },
        complete: () => {},
      });

      Sentry.addBreadcrumb({
        category: 'subscription',
        message: 'Subscribing to ProcessDisplaySystem',
        level: 'info',
        data: {
          processDisplaySystem: state.processDisplaySystem?.uuid,
        },
      });
    },
    logMessage: ({ state }, payload) => {
      Sentry.addBreadcrumb({
        category: 'app',
        message: payload.message,
        level: 'info',
        data: {
          processDisplaySystemUUID: state.uuid,
        },
      });
    },
    // getInitialData is called serveral times, like when network comes online
    getInitialData: ({ state, commit, dispatch }) => {
      // commit('updateAppLoading', true)
      Sentry.addBreadcrumb({
        category: 'data',
        message: 'Getting initial data',
        level: 'info',
        data: {
          processDisplaySystemUUID: state.uuid,
        },
      });
      const body = {
        query:
          "query initialPDS($uuid: String) {\n" +
          "  processDisplaySystem(uuid: $uuid) {\n" +
          "    uuid\n" +
          "    title\n" +
          "    debug\n" +
          "    organization {\n" +
          "      uuid\n" +
          "      label\n" +
          "      primary_color\n" +
          "      accent_color\n" +
          "    }\n" +
          "    channels {\n" +
          "      uuid\n" +
          "    }\n" +
          "    posChannels {\n" +
          "      uuid\n" +
          "    }\n" +
          "    location {\n" +
          "      label\n" +
          "      uuid\n" +
          "      ept\n" +
          "      timezone\n" +
          "    }\n" +
          "    categories {\n" +
          "      uuid\n" +
          "    }\n" +
          "    states\n" +
          "    stateColors {\n" +
          "      new\n" +
          "      pending\n" +
          "      processing\n" +
          "      completed\n" +
          "      delivered\n" +
          "    }\n" +
          "    readAccessToken {\n" +
          "      token\n" +
          "    }\n" +
          "    timeOfRed\n" +
          "    timeOfYellow\n" +
          "    clickableLineItems\n" +
          "    colorWholeCard\n" +
          "    showLocationVariationOnOrderCard\n" +
          "    darkMode\n" +
          "    printers {\n" +
          "       model\n" +
          "       ipAddress\n" +
          "    }\n" +
          "  }\n" +
          "}",
        variables: {
          uuid: state.uuid,
        },
      };

      return axios
        .post(state.graphURL, body)
        .then(async (response) => {
          if (!response.data || !response.data.data) {
            throw new Error('Malformed response payload from getInitialData');
          }
          if (response.data.data.processDisplaySystem) {
            commit(
              "updateOrganization",
              response.data.data.processDisplaySystem.organization
            );
            commit(
              "setProcessDisplaySystem",
              response.data.data.processDisplaySystem
            );

            if (state.socketClient === null) {
              const wsClientOptions = getWsClientOptions(state, commit, dispatch)
              commit(
                "updateSocketClient",
                createClient(wsClientOptions)
              );
            }

            if (
              axios.defaults.headers.Authorization === null ||
              axios.defaults.headers.Authorization === undefined
            ) {
              axios.defaults.headers['Authorization'] = "ReadAccessToken " +
                  response.data.data.processDisplaySystem.readAccessToken.token;
              axios.defaults.headers['SalesCloud-Organization'] = response.data.data.processDisplaySystem.organization.uuid;
            }

            await dispatch("getOrderData");

            if (
              response.data.data.processDisplaySystem !== null &&
              response.data.data.processDisplaySystem !== undefined &&
              state.subscribedToPDS === false
            ) {
              await dispatch(
                "subscribeToProcessDisplaySystem",
                response.data.data.processDisplaySystem
              );
            }
          }
        })
        .catch((error) => {
          Sentry.captureException(error);
        })
        .finally(() => {
          dispatch("getOrganizationData");
          commit("updateAppLoading", false);
        });
    },
    getOrganizationData({ state, commit }) {
      const body = {
        query:
          "query OrganizationData($organization: String!, $type: String!) {\n" +
          "  locationOptions(organization: $organization) {\n" +
          "    uuid\n" +
          "    label\n" +
          "    description\n" +
          "    ept\n" +
          "    timezone\n" +
          "  }\n" +
          "  channelsList(organization: $organization) {\n" +
          "    label\n" +
          "    uuid\n" +
          "    description\n" +
          "  }\n" +
          "  categories(type: $type, uuid: $organization) {\n" +
          "    uuid\n" +
          "    name\n" +
          "  }\n" +
          "  itemsList(organization: $organization) {\n" +
          "    uuid\n" +
          "    title\n" +
          "    categories {\n" +
          "      uuid\n" +
          "      name\n" +
          "    }\n" +
          "  }\n" +
          "}",
        variables: {
          organization: state.processDisplaySystem.organization.uuid,
          type: "products",
        },
      };

      return axios
        .post(state.graphURL, body)
        .then((response) => {
          if (response.data.data.locationOptions !== null) {
            commit("updateLocationOptions", response.data.data.locationOptions);
          }
          if (response.data.data.channelsList !== null) {
            commit("updateChannelOptions", response.data.data.channelsList);
          }
          if (response.data.data.categories !== null) {
            commit("updateCategoryOptions", response.data.data.categories);
          }
          if (response.data.data.itemsList !== null) {
            commit("updateItemMap", response.data.data.itemsList);
          }
        })
        .catch(() => {
          return null;
        });
    },
    getProcessDisplayData({ state, commit, dispatch }) {
      Sentry.addBreadcrumb({
        category: 'data',
        message: 'Get process display system',
        level: 'info',
        data: {
          processDisplaySystemUUID: state.uuid,
        },
      });
      const body = {
        query:
          "query getPDS($uuid: String) {\n" +
          "  processDisplaySystem(uuid: $uuid) {\n" +
          "    uuid\n" +
          "    title\n" +
          "    debug\n" +
          "    organization {\n" +
          "      uuid\n" +
          "      label\n" +
          "      primary_color\n" +
          "      accent_color\n" +
          "    }\n" +
          "    channels {\n" +
          "      uuid\n" +
          "    }\n" +
          "    posChannels {\n" +
          "      uuid\n" +
          "    }\n" +
          "    location {\n" +
          "      label\n" +
          "      uuid\n" +
          "      ept\n" +
          "      timezone\n" +
          "    }\n" +
          "    categories {\n" +
          "      uuid\n" +
          "    }\n" +
          "    states\n" +
          "    stateColors {\n" +
          "      new\n" +
          "      pending\n" +
          "      processing\n" +
          "      completed\n" +
          "      delivered\n" +
          "    }\n" +
          "    readAccessToken {\n" +
          "      token\n" +
          "    }\n" +
          "    timeOfRed\n" +
          "    timeOfYellow\n" +
          "    clickableLineItems\n" +
          "    colorWholeCard\n" +
          "    showLocationVariationOnOrderCard\n" +
          "    darkMode\n" +
          "    printers {\n" +
          "       model\n" +
          "       ipAddress\n" +
          "    }\n" +
          "  }\n" +
          "}",
        variables: {
          uuid: state.uuid,
        },
      };

      return axios
        .post(state.graphURL, body)
        .then(async (response) => {
          if (
            response &&
            response.data &&
            response.data.data &&
            response.data.data.processDisplaySystem
          ) {
            commit(
              "updateOrganization",
              response.data.data.processDisplaySystem.organization
            );
            commit(
              "setProcessDisplaySystem",
              response.data.data.processDisplaySystem
            );

            if (state.socketClient === null) {
              const wsClientOptions = getWsClientOptions(state, commit, dispatch)
              const wsClient = createClient(wsClientOptions)
              commit(
                "updateSocketClient",
                wsClient
              );
            }

            if (
              axios.defaults.headers.Authorization === null ||
              axios.defaults.headers.Authorization === undefined
            ) {
              axios.defaults.headers['Authorization'] = "ReadAccessToken " +
                  response.data.data.processDisplaySystem.readAccessToken.token;
              axios.defaults.headers['SalesCloud-Organization'] =
                  response.data.data.processDisplaySystem.organization.uuid;
            }

            dispatch("getOrganizationData");

            if (
              response.data.data.processDisplaySystem !== null &&
              response.data.data.processDisplaySystem !== undefined &&
              state.subscribedToPDS === false
            ) {
              await dispatch(
                "subscribeToProcessDisplaySystem",
                response.data.data.processDisplaySystem
              );
            }
            return response.data.data.processDisplaySystem;
          }
          return null;
        })
        .catch((error) => {
          Sentry.captureException(error);

          return null;
        });
    },
    subscribeOrderData({ state, commit, getters, dispatch }) {

      Sentry.addBreadcrumb({
        category: 'subscription',
        message: 'Subscribing to order data',
        level: 'info',
        data: {
          processDisplaySystemUUID: state.uuid,
        },
      });

      const startTimeInMillis = getters.startTimeInMillis;

      const conditions = [];

      if (
        state.processDisplaySystem !== null &&
        state.processDisplaySystem !== undefined &&
        state.processDisplaySystem.location !== null &&
        state.processDisplaySystem.location !== undefined &&
        state.processDisplaySystem.location.uuid !== null &&
        state.processDisplaySystem.location.uuid !== undefined
      ) {
        conditions.push({
          property: "location.uuid",
          operator: "equals",
          value: state.processDisplaySystem.location.uuid,
        });
      }

      if (
        state.processDisplaySystem !== null &&
        state.processDisplaySystem !== undefined &&
        state.processDisplaySystem.channels !== null &&
        state.processDisplaySystem.channels !== undefined &&
        Array.isArray(state.processDisplaySystem.channels) && 
        state.processDisplaySystem.channels.length > 0
      ) {
        conditions.push({
          property: "channel.uuid",
          operator: "equals",
          values: state.processDisplaySystem.channels.map((channel) => {
            return channel.uuid;
          }),
        });
      }

      const body = {
        query:
          "subscription ($startTimeInMillis: Float!, $conditions: [QueryCondition]) {\n" +
          "  orderChanged(startTimeInMillis: $startTimeInMillis, conditions: $conditions) {\n" +
          "    uuid\n" +
          "    state\n" +
          "    created\n" +
          "    fulfillment\n" +
          "	   accountingTime\n" +
          "    orderId\n" +
          "    sequence\n" +
          "    origin\n" +
          "    balance {\n" +
          "       amount\n" +
          "       currency_code\n" +
          "    }\n" +
          "	 payments {\n " +
          "		status\n" +
          "		created\n" +
          "		entangleKey\n" +
          "	 }\n" +
          "    lineItems {\n" +
          "       quantity\n" +
          "       uuid\n" +
          "       label\n" +
          "       dynamicDescription\n" +
          "       status\n" +
          "       data\n" +
          "       item {\n" +
          "      	  uuid\n" +
          "         categories{\n" +
          "           uuid\n" +
          "         }\n" +
          "       }\n" +
          "       totalPrice {\n" +
          "         amount\n" +
          "         currency_code\n" +
          "       }\n" +
          "    }\n" +
          "    email\n" +
          "    comment\n" +
          "    data\n" +
          "    locationVariation {\n" +
          "    	 label\n" +
          "    }\n" +
          "    location {\n" +
          "    	 uuid\n" +
          "    	 label\n" +
          "    }\n" +
          "    deliveryTime\n" +
          "	 deliveryTimeInMillis\n" +
          "    customer {\n" +
          "      email\n" +
          "      phone\n" +
          "      address {\n" +
          "        first_name\n" +
          "        last_name\n" +
          "        name_line\n" +
          "      }\n" +
          "    }\n" +
          "  }\n" +
          "}",
        variables: {
          startTimeInMillis: startTimeInMillis,
          conditions: conditions,
        },
      };

      state.socketClient.subscribe(body, {
        next: (data) => {
          commit("setProcessing", true);
          setTimeout(() => {
            commit("setProcessing", false);
          }, 500);

          // try {
          // 	// const statesToListen = ['pending', 'new']
          // 	// const ordersInStates = data.data.ordersChanged.filter(order => statesToListen.includes(order.state))
          // 	// if(ordersInStates.length > state.orders.length) {
          // 	// 	NativeAudio.play({
          // 	// 		assetId: 'ding2',
          // 	// 	});
          // 	// }
          // 	// commit('updateOrders', data.data.ordersChanged)
          // }
          // catch(e) {
          // 	// Do nothing
          // }
          commit("updateOrderChanged", data.data.orderChanged);
        },
        error: (error) => {
          Sentry.addBreadcrumb({
            category: 'subscription',
            message: 'orderChanged subscription error',
            level: 'info',
            data: {
              processDisplaySystemUUID: state.uuid,
              error: error instanceof Error ? error.message : error,
            },
          });

          Sentry.captureException(error);
        },
        complete: () => {
          dispatch("subscribeOrderData");
        },
      });
    },
    subscribePosOrderData({ state, commit, dispatch }) {
      console.debug('VueX Action: subscribePosOrderData called');

      Sentry.addBreadcrumb({
        category: 'subscription',
        message: 'Subscribing to pos order data',
        level: 'info',
        data: {
          processDisplaySystemUUID: state.uuid,
        },
      });

      const body = {
        query:
          "subscription ($channels: [String!]) {\n" +
          "  posOrderChanged(channels: $channels) {\n" +
          "    uuid\n" +
          "    state\n" +
          "    created\n" +
          "    sequence\n" +
          "    fulfillment\n" +
          "    origin\n" +
          "    accountingTime\n" +
          "    balance {\n" +
          "       amount\n" +
          "       currency_code\n" +
          "    }\n" +
          "	 payments {\n " +
          "		status\n" +
          "		created\n" +
          "		entangleKey\n" +
          "	 }\n" +
          "    tags {\n" +
          "       name\n" +
          "    }\n" +
          "    locationVariation {\n" +
          "    	 label\n" +
          "    }\n" +
          "    location {\n" +
          "    	 uuid\n" +
          "    	 label\n" +
          "    }\n" +
          "    lineItems {\n" +
          "       quantity\n" +
          "       uuid\n" +
          "       label\n" +
          "       created\n" +
          "       dynamicDescription\n" +
          "       status\n" +
          "       printed\n" +
          "       data\n" +
          "       comment\n" +
          "       item {\n" +
          "      	  uuid\n" +
          "      	  categories {\n" +
          "      	  	uuid\n" +
          "      	  	name\n" +
          "         }\n" +
          "       }\n" +
          "       totalPrice {\n" +
          "         amount\n" +
          "         currency_code\n" +
          "       }\n" +
          "    }\n" +
          "    email\n" +
          "    comment\n" +
          "  }\n" +
          "}",
        variables: {
          channels: state.processDisplaySystem.posChannels.map(
            (channel) => channel.uuid
          ),
        },
      };

      state.socketClient.subscribe(body, {
        next: (data) => {
          commit("setProcessing", true);
          setTimeout(() => {
            commit("setProcessing", false);
          }, 500);
          commit("updatePosOrderChanged", data.data.posOrderChanged);
        },
        error: (error) => {
          Sentry.addBreadcrumb({
            category: 'subscription',
            message: 'posOrderChanged subscription error',
            level: 'info',
            data: {
              processDisplaySystemUUID: state.uuid,
              error: error instanceof Error ? error.message : error,
            },
          });

          Sentry.captureException(error);
        },
        complete: () => {
          console.log('posOrderChanged -> complete');
          dispatch("subscribePosOrderData");
        },
      });
    },
    getOrderData({ state, commit, getters }) {
      Sentry.addBreadcrumb({
        category: 'data',
        message: 'Fetching order data',
        level: 'info',
        data: {
          processDisplaySystemUUID: state.uuid,
        },
      });

      const startTimeInMillis = getters.startTimeInMillis;

      const body = {
        query:
          "query($organization: String! $location: String!, $startTimeInMillis: Float!, $conditions: [QueryCondition], $posChannels: [String!]) {\n" +
          "  posOrders(channels: $posChannels) {\n" +
          "    uuid\n" +
          "    state\n" +
          "    created\n" +
          "	   accountingTime\n" +
          "    fulfillment\n" +
          "    sequence\n" +
          "    origin\n" +
          "	 payments {\n " +
          "		status\n" +
          "		created\n" +
          "		entangleKey\n" +
          "	 }\n" +
          "    balance {\n" +
          "       amount\n" +
          "       currency_code\n" +
          "    }\n" +
          "    tags {\n" +
          "       name\n" +
          "    }\n" +
          "    locationVariation {\n" +
          "    	 label\n" +
          "    }\n" +
          "    location {\n" +
          "    	 uuid\n" +
          "    	 label\n" +
          "    }\n" +
          "    lineItems {\n" +
          "       quantity\n" +
          "       uuid\n" +
          "       label\n" +
          "       created\n" +
          "       printed\n" +
          "       comment\n" +
          "       dynamicDescription\n" +
          "       status\n" +
          "       data\n" +
          "       totalPrice {\n" +
          "         amount\n" +
          "         currency_code\n" +
          "       }\n" +
          "       item {\n" +
          "      	  uuid\n" +
          "      	  categories {\n" +
          "      	  	uuid\n" +
          "      	  	name\n" +
          "         }\n" +
          "       }\n" +
          "    }\n" +
          "    email\n" +
          "    comment\n" +
          "    data\n" +
          "  }\n" +
          "  orders(startTimeInMillis: $startTimeInMillis, conditions: $conditions) {\n" +
          "    uuid\n" +
          "    state\n" +
          "    created\n" +
          "	   accountingTime\n" +
          "    fulfillment\n" +
          "    orderId\n" +
          "    coupon {\n" +
          "      uuid\n" +
          "      percentOff\n" +
          "    }\n" +
          "    sequence\n" +
          "    origin\n" +
          "    balance {\n" +
          "       amount\n" +
          "       currency_code\n" +
          "    }\n" +
          "	 payments {\n " +
          "		status\n" +
          "		created\n" +
          "		entangleKey\n" +
          "	 }\n" +
          "    locationVariation {\n" +
          "    	 label\n" +
          "    }\n" +
          "    location {\n" +
          "    	 uuid\n" +
          "    	 label\n" +
          "    }\n" +
          "    lineItems {\n" +
          "       quantity\n" +
          "       uuid\n" +
          "       label\n" +
          "       dynamicDescription\n" +
          "       status\n" +
          "       data\n" +
          "       item {\n" +
          "      	  uuid\n" +
          "         categories{\n" +
          "           uuid\n" +
          "         }\n" +
          "       }\n" +
          "       totalPrice {\n" +
          "         amount\n" +
          "         currency_code\n" +
          "       }\n" +
          "    }\n" +
          "    email\n" +
          "    comment\n" +
          "    data\n" +
          "    deliveryTime\n" +
          "    deliveryTimeInMillis\n" +
          "    customer {\n" +
          "      email\n" +
          "      phone\n" +
          "      address {\n" +
          "        first_name\n" +
          "        last_name\n" +
          "        name_line\n" +
          "      }\n" +
          "    }\n" +
          "  }\n" +
          "	 location ( organization: $organization uuid: $location ) { \n" +
          "    label\n" +
          "    ept\n" +
          "  }\n" +
          "}",
        variables: {
          organization: state.organizationUUID,
          location: state.processDisplaySystem.location.uuid,
          startTimeInMillis: startTimeInMillis,
          posChannels: state.processDisplaySystem.posChannels.map((channel) => {
            return channel.uuid;
          }),
          conditions: [
            {
              property: "channel.uuid",
              operator: "equals",
              values: state.processDisplaySystem.channels.map((channel) => {
                return channel.uuid;
              }),
            },
            {
              property: "location.uuid",
              operator: "equals",
              value: state.processDisplaySystem.location.uuid,
            },
          ],
        },
      };

      return axios
        .post(state.graphURL, body)
        .then((response) => {
          if (response.data.data) {
            commit("updateOrders", response.data.data.orders);
            commit("updatePosOrders", response.data.data.posOrders);
            return response.data.data.orders;
          }
        })
        .catch(() => {
          // Added a missing catch
          return null;
        });
    },
    updateOrderState: ({ state }, payload) => {
      const body = {
        query:
          "mutation {\n" +
          '  updateOrderState(uuid: "' +
          payload.uuid +
          '", state: ' +
          payload.state +
          ") \n" +
          "}",
      };

      return axios
        .post(state.graphURL, body)
        .then((response) => {
          if (response && response.data && response.data.data) {
            return response.data.data.updateOrderState;
          }
          return false;
        })
        .catch(() => {
          return false;
        });
    },
    updatePosOrderState: ({ state }, payload) => {
      const body = {
        query:
          "mutation {\n" +
          '  updatePosOrderState(uuid: "' +
          payload.uuid +
          '", state: ' +
          payload.state +
          ") \n" +
          "}",
      };

      return axios
        .post(state.graphURL, body)
        .then((response) => {
          if (response && response.data && response.data.data) {
            return response.data.data.updatePosOrderState;
          }
          return false;
        })
        .catch(() => {
          return false;
        });
    },
    updateLineItemStatus: ({ state }, { uuid, newStatus }) => {
      const body = {
        query:
          "mutation UpdateLineItem {\n" +
          '  updatePosLineItemState(uuid: "' +
          uuid +
          '", state: "' +
          newStatus +
          '")\n' +
          "}",
      };
      return axios.post(state.graphURL, body).then(() => {
        return true;
      });
    },
    moveSelectedOrderStates: async ({ state, commit }, direction) => {
      //@property {string} direction    - Direction to move order. Can be either 'forward' or 'backward'
      if (direction === null || typeof direction === "undefined") {
        direction = "forward";
      }
      const orderStates = state.orderStates.reduce(
        (a, v) => ({ ...a, [v.key]: v }),
        {}
      );
      for (let i = 0; i < state.selectedOrders.length; i++) {
        const order = state.selectedOrders[i];
        let orderState = state.selectedOrders[i].state;
        if (direction === "backward") {
          if (
            orderStates[order.state] === null ||
            typeof orderStates[order.state] === "undefined" ||
            orderStates[order.state].previousStep === null ||
            typeof orderStates[order.state].previousStep === "undefined"
          ) {
            continue;
          }
          orderState = orderStates[order.state].previousStep;
        }
        if (direction === "forward") {
          if (
            orderStates[order.state] === null ||
            typeof orderStates[order.state] === "undefined" ||
            orderStates[order.state].nextStep === null ||
            typeof orderStates[order.state].nextStep === "undefined"
          ) {
            continue;
          }
          orderState = orderStates[order.state].nextStep;
        }

        let isPosOrder = true;

        if (
          typeof state.orders !== "undefined" &&
          state.orders !== null &&
          state.orders.find((orderToFind) => orderToFind.uuid === order.uuid)
        ) {
          isPosOrder = false;
        }

        if (isPosOrder) {
          commit("setPosOrderState", { uuid: order.uuid, state: orderState });

          const body = {
            query:
              "mutation {\n" +
              '  updatePosOrderState(uuid: "' +
              order.uuid +
              '", state: ' +
              orderState +
              ") \n" +
              "}",
          };
          return axios
            .post(state.graphURL, body)
            .then((response) => {
              if (
                response &&
                response.data &&
                response.data.data &&
                response.data.data.updatePosOrderState
              ) {
                return orderState;
              }
              return order.state;
            })
            .catch(() => {
              return order.state;
            });
        } else {
          commit("setOrderState", { uuid: order.uuid, state: orderState });

          const body = {
            query:
              "mutation {\n" +
              '  updateOrderState(uuid: "' +
              order.uuid +
              '", state: ' +
              orderState +
              ") \n" +
              "}",
          };
          return axios
            .post(state.graphURL, body)
            .then((response) => {
              if (
                response &&
                response.data &&
                response.data.data &&
                response.data.data.updateOrderState
              ) {
                return orderState;
              }
              return order.state;
            })
            .catch(() => {
              return order.state;
            });
        }
      }
    },
    updateProcessDisplaySystem: (
      { state, commit },
      {
        title,
        states,
        categories,
        location,
        channels,
        timeOfRed,
        timeOfYellow,
        clickableLineItems,
        stateColors,
        colorWholeCard,
        showLocationVariationOnOrderCard,
        darkMode,
      }
    ) => {
      const pds = {
        uuid: state.uuid,
      };
      if (title !== null && typeof title !== "undefined") {
        pds.title = title;
      }
      if (Array.isArray(states)) {
        pds.states = states;
      }
      if (Array.isArray(categories)) {
        pds.categories = categories;
      }
      if (Array.isArray(location)) {
        pds.location = location;
      }
      if (Array.isArray(channels)) {
        pds.channels = channels;
      }
      if (timeOfRed !== null && typeof timeOfRed !== "undefined") {
        pds.timeOfRed = timeOfRed;
      }
      if (timeOfYellow !== null && typeof timeOfYellow !== "undefined") {
        pds.timeOfYellow = timeOfYellow;
      }
      if (
        clickableLineItems !== null &&
        typeof clickableLineItems !== "undefined"
      ) {
        pds.clickableLineItems = clickableLineItems;
      }
      if (stateColors !== null && typeof stateColors !== "undefined") {
        pds.stateColors = stateColors;
      }
      if (colorWholeCard !== null && typeof colorWholeCard !== "undefined") {
        pds.colorWholeCard = colorWholeCard;
      }
      if (
        showLocationVariationOnOrderCard !== null &&
        typeof showLocationVariationOnOrderCard !== "undefined"
      ) {
        pds.showLocationVariationOnOrderCard = showLocationVariationOnOrderCard;
      }
      if (darkMode !== null && typeof darkMode !== "undefined") {
        pds.darkMode = darkMode;
      }
      const body = {
        query:
          "mutation UpdateProcessDisplaySystem($processDisplaySystem: UpdateProcessDisplaySystem) {\n" +
          "  updateProcessDisplaySystem(processDisplaySystem: $processDisplaySystem) {\n" +
          "    uuid\n" +
          "    title\n" +
          "    organization {\n" +
          "      uuid\n" +
          "      label\n" +
          "      primary_color\n" +
          "      accent_color\n" +
          "    }\n" +
          "    channels {\n" +
          "      uuid\n" +
          "    }\n" +
          "    posChannels {\n" +
          "      uuid\n" +
          "    }\n" +
          "    location {\n" +
          "      label\n" +
          "      uuid\n" +
          "      ept\n" +
          "      timezone\n" +
          "    }\n" +
          "    categories {\n" +
          "      uuid\n" +
          "    }\n" +
          "    states\n" +
          "    stateColors {\n" +
          "      new\n" +
          "      pending\n" +
          "      processing\n" +
          "      completed\n" +
          "      delivered\n" +
          "    }\n" +
          "    readAccessToken {\n" +
          "      token\n" +
          "    }\n" +
          "    timeOfRed\n" +
          "    timeOfYellow\n" +
          "    clickableLineItems\n" +
          "    colorWholeCard\n" +
          "    showLocationVariationOnOrderCard\n" +
          "    darkMode\n" +
          "  }\n" +
          "}",
        variables: {
          processDisplaySystem: pds,
        },
      };

      return axios.post(state.graphURL, body).then((response) => {
        commit(
          "setProcessDisplaySystem",
          response.data.data.updateProcessDisplaySystem
        );
        return response.data.data.updateProcessDisplaySystem;
      });
    },
  },
  modules: {},
});
